import axios from 'axios';
import useInsightBotStore from '../store/insightBotStore';

const BASE_URL = (process.env.REACT_APP_INSIGHT_BOT_API_URL || '').replace(/\/+$/, '');
const WORKSPACE_ID = process.env.REACT_APP_INSIGHT_BOT_WORKSPACE_ID || '';

const DEFAULT_TIMEOUT_MS = 30000;
const QUERY_TIMEOUT_MS = 8 * 60 * 1000; // analysis queries can take up to ~8 minutes

const insightBotApi = axios.create({
  baseURL: BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
});

// Attach Bearer token + workspace header on every request (except login)
insightBotApi.interceptors.request.use((config) => {
  const requestUrl = String(config.url || '');
  if (requestUrl.includes('/api/auth/login')) {
    return config;
  }

  const { accessToken } = useInsightBotStore.getState();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (WORKSPACE_ID) {
    config.headers['X-Workspace-Id'] = WORKSPACE_ID;
  }
  return config;
});

let loginPromise = null;

// Logs the chatbot widget in using the configured service-account credentials.
export function loginInsightBot() {
  if (loginPromise) return loginPromise;

  loginPromise = insightBotApi
    .post('/api/auth/login', {
      email: process.env.REACT_APP_INSIGHT_BOT_EMAIL,
      password: process.env.REACT_APP_INSIGHT_BOT_PASSWORD,
      tenant_slug: process.env.REACT_APP_INSIGHT_BOT_TENANT_SLUG || 'default',
    })
    .then((response) => {
      const data = response.data?.data || response.data;
      useInsightBotStore.getState().setTokens(data.access_token, data.refresh_token);
      return data;
    })
    .finally(() => {
      loginPromise = null;
    });

  return loginPromise;
}

// Response interceptor: on 401, log in again once and retry the request.
insightBotApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};
    const requestUrl = String(config.url || '');
    const isAuthEndpoint = requestUrl.includes('/api/auth/login');

    if (error.response?.status === 401 && !isAuthEndpoint && !config._retried) {
      config._retried = true;
      try {
        await loginInsightBot();
        return insightBotApi(config);
      } catch (loginError) {
        useInsightBotStore.getState().clearAuth();
        return Promise.reject(loginError);
      }
    }

    return Promise.reject(error);
  }
);

export function getInsightBotMe() {
  return insightBotApi.get('/api/auth/me');
}

export function switchInsightBotScope(data) {
  return insightBotApi.post('/api/auth/switch-scope', data);
}

export function getInsightBotSchema(params = {}) {
  return insightBotApi.get('/api/schema', { params });
}

export function chatWithInsightBot({ message, sessionId }) {
  const payload = { message };
  if (sessionId) payload.session_id = sessionId;

  return insightBotApi.post('/api/assistant/chat', payload, { timeout: QUERY_TIMEOUT_MS });
}

export function resetInsightBotSession(sessionId) {
  return insightBotApi.post('/api/assistant/reset', null, { params: { session_id: sessionId } });
}

export function isInsightBotConfigured() {
  return Boolean(BASE_URL && process.env.REACT_APP_INSIGHT_BOT_EMAIL && process.env.REACT_APP_INSIGHT_BOT_PASSWORD);
}

export default insightBotApi;
